import { ConfigurationWithCategories, ContentController, ControllerHelper, PALMSModule, PlayListNodeDownloadDto } from 'collaboration-service';
import InfoBox from 'components/InfoBox/InfoBox';
import { Button, Icon, LoaderInline, RadioButton, Theme, useThemePart } from 'imaginarity-react-ui';
import _ from 'lodash';
import * as React from 'react';
import { useAppSelector } from 'services/ApplicationState/ApplicationState';
import { countElementUsageInNodes, shallowCompare, visit } from 'services/Helpers';
import { useImgI18N } from 'services/ImgI18N';
import GlobalPostSearch from 'views/GeneralPostSearch/GeneralPostSearch';
import PlaylistEditContentImplementedElement, { getPlaylistContentData } from 'views/Playlist/Edit/PlaylistEditContentImplementedElement';
import { PlaylistEditState, useSelectorPLE } from './PlaylistEdit';
import { AGCTitle, AGCTitleList, AGCTitleRadio, StickyContents } from './PlaylistEditClasses';
import { PlaylistEditSequenceLinearSC as T } from "./PlaylistEditSequenceLinearSC";
import { usePLEState } from './usePLEState';

type ContentTypes = "TubePost" | "Quiz" | "WikiArticle" | "ContentPost" | "BestPracticePost" | "PorscheMomentPost" | "Podcast" | "FileEntry" | "PALMS" | "SlideShow" | "Playlist";
type OrderbyTypes = "Alphabetical" | "Type";
export interface PlaylistEditContentState {
    orderBy: OrderbyTypes;
    contentType: ContentTypes;
    term: string;
}
export interface PlaylistEditContentProps {
}

const pleMapper = (state: PlaylistEditState) => ({
    playlistId: state.playlist?.id,
    posts: state.posts,
    missing: state.missing.content,
});

const initExclCats = ["c763e302-87e3-44c1-a81e-b6f819aacb27"];

const PlaylistEditContent = (p: PlaylistEditContentProps) => {
    const { t, currentLanguage } = useImgI18N("playlist");
    const contentLanguage = useAppSelector(s => s.contentLanguage);
    const [orderBy, setOrderBy] = React.useState<OrderbyTypes>("Type");
    const [showPostSearch, setShowPostSearch] = React.useState<boolean>(false);
    const [excludeCats, setExcludeCats] = React.useState<string[]>(initExclCats);
    const lng = contentLanguage ?? currentLanguage;

    const s_startNode = usePLEState(x => x.startNode);
    const s_contents = usePLEState(x => x.contents);
    const { posts, missing } = useSelectorPLE(pleMapper, shallowCompare);

    const nodes: PlayListNodeDownloadDto[] = [];
    visit(s_startNode.value, (cur) => _.compact(_.map(cur.children, edge => edge.to)), cur => nodes.push(cur));

    const cHeight = useThemePart((t: Theme) => t.content.height) - 151;

    const loading = posts === undefined;

    React.useEffect(() => {
        const l = async () => {
            setExcludeCats(initExclCats);
            const data = await ControllerHelper.singleCall({ grouptype: "CMS" }, ContentController.GetCategoriesForAllGroupsByGroupTypeWithConfigurations, true);
            if (data) {
                const exclude = _.find(data.configurations, (c: ConfigurationWithCategories) => _.find(c.configuration.names, n => n.text === "Don't use for") !== undefined);
                if (exclude) {
                    const excludeCatIds = _.map(exclude.categories, c => c.id);
                    setExcludeCats(excludeCatIds);
                }
            }
        }
        l();

    }, []);

    // Kombiniertes useMemo für alle s_contents-Abhängigkeiten
    const memoizedContentHelpers = React.useMemo(() => {
        const toggleHidden = (idx: number) => (hidden: boolean) => {
            const newContents = _.clone(s_contents.value ?? []);
            if (newContents[idx]) {
                newContents[idx].hidden = hidden;
            }
            s_contents.setValue(newContents);
        };

        const addSelectedArticle = (ids: string[]) => {
            const newContents = _.clone(s_contents.value ?? []);
            _.forEach(ids, id => {
                newContents.push({
                    referenceId: id,
                    referenceType: "WikiArticle",
                })
            });
            s_contents.setValue(newContents);
        };

        const onPostAdded = (type: string) => (ids: string[]) => {
            const fes = _.filter(s_contents.value, c => c.referenceType === "FileEntry");
            const feIds = _.compact(_.map(fes, c => c?.referenceId ?? ""));
            const idsToRun = type === "FileEntry" ? [...feIds, ...ids] : ids;
            const newContents = _.clone(s_contents.value ?? []);
            _.forEach(idsToRun, id => {
                if (_.find(newContents, c => c.referenceId === id) === undefined)
                    newContents.push({
                        referenceId: id,
                        referenceType: type,
                    })
            });
            s_contents.setValue(newContents);
        };

        const onPALMSPostAdded = (trainings: PALMSModule[]) => {
            const newContents = _.clone(s_contents.value ?? []);
            _.forEach(trainings, training => {
                const id = training.id;
                if (_.find(newContents, c => c.referenceId === id) === undefined) {
                    newContents.push({
                        referenceId: id,
                        referenceType: "PALMS",
                        referenceData: JSON.stringify(training),
                    })
                    s_contents.setValue(newContents);
                }
            });
        };

        const removeSelected = (id: string) => () => {
            const newContents = _.clone(s_contents.value ?? []);
            const idx = _.findIndex(newContents, c => c.referenceId === id);
            if (newContents.length > idx) {
                newContents.splice(idx, 1);
                s_contents.setValue(newContents);
            }
        };

        return { toggleHidden, addSelectedArticle, onPostAdded, onPALMSPostAdded, removeSelected };
    }, [s_contents]);

    const toggleHidden = memoizedContentHelpers.toggleHidden;
    const addSelectedArticle = memoizedContentHelpers.addSelectedArticle;
    const onPostAdded = memoizedContentHelpers.onPostAdded;
    const onPALMSPostAdded = memoizedContentHelpers.onPALMSPostAdded;
    const removeSelected = memoizedContentHelpers.removeSelected;

    const contents = React.useMemo(() => {
        const cts = _.map(s_contents.value, pc => {
            const post = _.find(posts, s => s.id === pc.referenceId)!;
            if (post)
                return { contents: pc, post, title: getPlaylistContentData(pc, lng, post).title };
            return undefined;
        });
        return _.compact(cts);
    }, [s_contents, posts, lng]);

    const toShow = React.useMemo(() => {
        const sortedByAlphabet = _.sortBy(contents, c => c.title.toLocaleUpperCase());
        return (orderBy === "Alphabetical" ? sortedByAlphabet : _.sortBy(sortedByAlphabet, s => s.contents.referenceType));
    }, [contents, orderBy]);

    const groupedToShow = _.groupBy(toShow, 'contents.referenceType');

    const hasValues = React.useMemo(() => {
        return _.map(Object.keys(groupedToShow)).length > 0;
    }, [groupedToShow]);

    const referenceIds = _.compact(_.map(s_contents.value, c => c.referenceId));
    const numberOfUsedElements: number = _.sumBy(referenceIds, id => countElementUsageInNodes(nodes, id));
    const numberOfUnusedElements: number = referenceIds.length - numberOfUsedElements;

    console.log("exclCats = ", excludeCats);

    return (
        <>
            <T.ImplContainer height={cHeight} style={{
                paddingBottom: 0, paddingTop: 15, marginTop: -15, position: "relative"
            }}>
                <T.ImplContainerInfo style={{ columnGap: 20 }}>
                    <InfoBox
                        noBold
                        noUppercase
                        noLetterSpacing
                        type={hasValues ? 'default' : 'alert'}
                        icon="info"
                        content={hasValues ? t("to add more content to this playlist, click the button on the right.") : t("to add content to this playlist, click the button on the right.")}
                    />
                    <Button
                        icon="add"
                        kind={hasValues ? "secondary" : "primary"}
                        content={hasValues ? t("add more content") : t("add first content")}
                        onClick={() => setShowPostSearch(true)}
                    />
                </T.ImplContainerInfo>
                {hasValues && numberOfUnusedElements > 0 &&
                    <InfoBox
                        noBold
                        noUppercase
                        closeable
                        noLetterSpacing
                        type={'info'}
                        icon="info"
                        content={("the contents written in blue are not yet assigned to any phase..")}
                    />
                }
                <div>
                    <StickyContents>
                        {loading ?
                            <LoaderInline active infoText={t("loading selected elements...")} margin='10px 0 15px 0' />
                            :
                            (hasValues && <AGCTitle style={{ marginBottom: orderBy === "Alphabetical" ? 24 : 15, paddingTop: 10 }}>
                                <AGCTitleRadio selected={false}>{t("sort selected content")}</AGCTitleRadio>
                                <AGCTitleRadio selected={orderBy === "Type"}>
                                    <RadioButton selected={orderBy === "Type"} ingroup
                                        label={t('by type')}
                                        onClick={() => setOrderBy("Type")} />
                                </AGCTitleRadio>
                                <AGCTitleRadio selected={orderBy === "Alphabetical"}>
                                    <RadioButton selected={orderBy === "Alphabetical"} ingroup
                                        label={t('alphabetical')}
                                        onClick={() => setOrderBy("Alphabetical")} />
                                </AGCTitleRadio>
                            </AGCTitle>
                            )
                        }

                        {missing.contents &&
                            <AGCTitle missingInput style={{ fontWeight: "normal", fontSize: "1em" }}>
                                {t("no element selected")}
                                <Icon name="exclamation circle" color="@accent" marginTop={5} style={{ marginRight: 5, float: "left" }} />
                            </AGCTitle>
                        }
                        {orderBy === "Alphabetical" ?
                            _.map(toShow, (what, i) => {
                                const numberOfUsed: number = countElementUsageInNodes(nodes, what.contents?.referenceId ?? '');

                                return (
                                    <div key={i}>
                                        {i === 0 && <AGCTitleList height={0} />}
                                        <PlaylistEditContentImplementedElement
                                            content={what.contents}
                                            post={what.post}
                                            key={what.post?.id}
                                            onRemove={removeSelected(what.contents.referenceId ?? "")}
                                            toggleHidden={toggleHidden(i)}
                                            numberOfUsed={numberOfUsed}
                                            sortByType={false}
                                        />
                                    </div>
                                )
                            })
                            :
                            _.map(Object.keys(groupedToShow), referenceType => (
                                <div key={referenceType}>
                                    {<AGCTitleList height={30}>{t(referenceType)}</AGCTitleList>}
                                    {groupedToShow[referenceType].map((what, i) => {
                                        const numberOfUsed: number = countElementUsageInNodes(nodes, what.contents?.referenceId ?? '');
                                        return (
                                            <PlaylistEditContentImplementedElement
                                                content={what.contents}
                                                post={what.post}
                                                key={what.post?.id}
                                                onRemove={removeSelected(what.contents.referenceId ?? "")}
                                                toggleHidden={toggleHidden(i)}
                                                numberOfUsed={numberOfUsed}
                                                sortByType={true}
                                            />
                                        );
                                    })}
                                </div>
                            ))
                        }
                    </StickyContents>
                </div>
            </T.ImplContainer>

            <GlobalPostSearch
                show={showPostSearch}
                onClose={() => setShowPostSearch(false)}
                preSelected={_.map(contents, c => c.contents.referenceId ?? "")}
                isMulti={true}
                excludeCategories={excludeCats}
                onSelect={values => {
                    const type = values?.[0]?.type;
                    const ids = _.map(values, v => v.id);
                    const datas = _.map(values, v => v.data);
                    switch (type) {
                        case "TubePost":
                        case "Podcast":
                        case "Quiz":
                        case "ContentPost":
                        case "BestPracticePost":
                        case "PorscheMomentPost":
                        case "SlideShow":
                        case "Playlist":
                        case "FileEntry":
                            onPostAdded(type)(ids);
                            break;
                        case "WikiArticle":
                            addSelectedArticle(ids)
                            break;
                        case "PALMS":
                            onPALMSPostAdded(datas);
                            break;
                    }
                    setShowPostSearch(false);
                }}
            />
        </>
    );
}

export default PlaylistEditContent;
